import type {
  AcceptInviteRequest,
  ApiErrorBody,
  ApiHealth,
  ApiMeta,
  AuditEventSummary,
  DeviceActivityItem,
  AuthMeResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ClaimDeviceRequest,
  ClaimDeviceResponse,
  ClientUserSummary,
  CreateClientUserRequest,
  CreateDeviceGroupRequest,
  UpdateDeviceGroupRequest,
  CreateDeviceSkuRequest,
  DeleteDeviceRequest,
  DeviceProfileDetail,
  DeviceProfileSummary,
  ReplaceDeviceProfileRequest,
  UploadDeviceProfileRequest,
  CommandSetSummary,
  CommandSetDetail,
  UploadCommandSetRequest,
  ReplaceCommandSetRequest,
  DeviceConfigResponse,
  OtaCommandsResponse,
  OtaLogEntry,
  CreateStaffUserRequest,
  DeviceGroupSummary,
  DeviceSkuSummary,
  DeviceSummary,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  DevicePoolSummary,
  CreateDevicePoolRequest,
  UpdateDevicePoolRequest,
  AssignDeviceToPoolRequest,
  DeviceIngressKeyResponse,
  IngressConnectionSummary,
  CreateIngressConnectionRequest,
  UpdateIngressConnectionRequest,
  IngressConnectionStatusEntry,
  DevicePoolDevicesResponse,
  SkuFirmwareResponse,
  FirmwareCheckinsResponse,
  UnassignedDeviceCandidate,
  InvitePreviewResponse,
  LoginRequest,
  LoginResponse,
  RegisterDeviceRequest,
  RegisterDeviceResponse,
  ReleaseDeviceRequest,
  ReleaseDeviceResponse,
  SessionUser,
  StaffUserSummary,
  TenantSummary,
  CreateTenantRequest,
  UpdateTenantRequest,
  UpdateClientUserRequest,
  UpdateDeviceSkuRequest,
  UpdateStaffUserRequest,
  UpdateDeviceProfileRequest,
  UpdateDeviceProfileResponse,
  DeviceProfileOptionsResponse,
  UpdateOtaProfileRequest,
  UpdateOtaProfileResponse,
  DeviceAlarmConfigResponse,
  UpdateDeviceAlarmRequest,
  UpdateDeviceAlarmResponse,
  AlarmPushStatusResponse,
  PingStatusResponse,
  ConnectionSummary,
  CreateConnectionRequest,
  CreateMqttChannelRequest,
  CreateOrUpdateMqttBrokerRequest,
  CreateSupportGrantRequest,
  CreateUpstreamFormatRequest,
  MqttBrokerSummary,
  MqttChannelSummary,
  SupportGrantSummary,
  UpdateConnectionRequest,
  UpdateMqttChannelRequest,
  UpdateUpstreamFormatRequest,
  UpstreamFormatDetail,
  UpstreamFormatKind,
  UpstreamFormatSummary,
} from '@ucc/shared';

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, 'Invalid JSON response');
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await parseJson<ApiErrorBody>(res).catch(
      (): ApiErrorBody => ({ error: res.statusText || 'Request failed' }),
    );
    throw new ApiError(res.status, body.error || res.statusText, body.code);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return parseJson<T>(res);
}

export function fetchHealth(): Promise<ApiHealth> {
  return request<ApiHealth>('/health');
}

export function fetchMeta(): Promise<ApiMeta> {
  return request<ApiMeta>('/api/v1/meta');
}

export function fetchAuthMe(): Promise<AuthMeResponse> {
  return request<AuthMeResponse>('/api/v1/auth/me');
}

export function login(body: LoginRequest): Promise<LoginResponse> {
  return request<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function logout(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/v1/auth/logout', { method: 'POST' });
}

export function fetchTenants(): Promise<{ tenants: TenantSummary[] }> {
  return request('/api/v1/tenants');
}

export function createTenant(body: CreateTenantRequest): Promise<{ tenant: TenantSummary }> {
  return request('/api/v1/tenants', { method: 'POST', body: JSON.stringify(body) });
}

export function updateTenant(
  id: string,
  body: UpdateTenantRequest,
): Promise<{ tenant: TenantSummary }> {
  return request(`/api/v1/tenants/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function fetchDevicePools(): Promise<{ pools: DevicePoolSummary[] }> {
  return request('/api/v1/device-pools');
}

export function createDevicePool(
  body: CreateDevicePoolRequest,
): Promise<{ pool: DevicePoolSummary }> {
  return request('/api/v1/device-pools', { method: 'POST', body: JSON.stringify(body) });
}

export function updateDevicePool(
  id: string,
  body: UpdateDevicePoolRequest,
): Promise<{ pool: DevicePoolSummary }> {
  return request(`/api/v1/device-pools/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteDevicePool(id: string): Promise<{ ok: boolean }> {
  return request(`/api/v1/device-pools/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchDevicePool(id: string): Promise<{ pool: DevicePoolSummary }> {
  return request(`/api/v1/device-pools/${encodeURIComponent(id)}`);
}

export function fetchDevicePoolDevices(
  id: string,
  opts: { search?: string; page?: number; pageSize?: number } = {},
): Promise<DevicePoolDevicesResponse> {
  const params = new URLSearchParams();
  if (opts.search) params.set('search', opts.search);
  if (opts.page) params.set('page', String(opts.page));
  if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
  const qs = params.toString();
  return request(`/api/v1/device-pools/${encodeURIComponent(id)}/devices${qs ? `?${qs}` : ''}`);
}

export function fetchUnassignedDeviceCandidates(
  search: string,
): Promise<{ devices: UnassignedDeviceCandidate[] }> {
  const params = new URLSearchParams({ search });
  return request(`/api/v1/device-pools-unassigned-devices?${params.toString()}`);
}

export function assignDeviceToPool(
  deviceId: string,
  body: AssignDeviceToPoolRequest,
): Promise<{ device: { id: string; uid: string; devicePoolId: string | null } }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/device-pool`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function fetchDeviceIngressKey(deviceId: string): Promise<DeviceIngressKeyResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ingress-key`);
}

export function fetchIngressConnections(): Promise<{ connections: IngressConnectionSummary[] }> {
  return request('/api/v1/ingress-connections');
}

export function fetchIngressConnection(
  id: string,
): Promise<{ connection: IngressConnectionSummary; inUse: boolean }> {
  return request(`/api/v1/ingress-connections/${encodeURIComponent(id)}`);
}

export function fetchIngressConnectionStatusLog(
  id: string,
): Promise<{ entries: IngressConnectionStatusEntry[] }> {
  return request(`/api/v1/ingress-connections/${encodeURIComponent(id)}/status-log`);
}

export function clearIngressConnectionStatusLog(id: string): Promise<{ ok: true; deleted: number }> {
  return request(`/api/v1/ingress-connections/${encodeURIComponent(id)}/status-log`, { method: 'DELETE' });
}

export function createIngressConnection(
  body: CreateIngressConnectionRequest,
): Promise<{ connection: IngressConnectionSummary }> {
  return request('/api/v1/ingress-connections', { method: 'POST', body: JSON.stringify(body) });
}

export function updateIngressConnection(
  id: string,
  body: UpdateIngressConnectionRequest,
): Promise<{ connection: IngressConnectionSummary }> {
  return request(`/api/v1/ingress-connections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteIngressConnection(id: string): Promise<{ ok: boolean }> {
  return request(`/api/v1/ingress-connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchDevices(): Promise<{ devices: DeviceSummary[] }> {
  return request('/api/v1/devices');
}

export function claimDevice(body: ClaimDeviceRequest): Promise<ClaimDeviceResponse> {
  return request('/api/v1/devices/claim', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function registerDevice(body: RegisterDeviceRequest): Promise<RegisterDeviceResponse> {
  return request('/api/v1/devices/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchDeviceSkus(opts?: { all?: boolean }): Promise<{ skus: DeviceSkuSummary[] }> {
  const q = opts?.all ? '?all=1' : '';
  return request(`/api/v1/devices/skus${q}`);
}

export function createDeviceSku(body: CreateDeviceSkuRequest): Promise<{ sku: DeviceSkuSummary }> {
  return request('/api/v1/devices/skus', { method: 'POST', body: JSON.stringify(body) });
}

export function updateDeviceSku(
  id: string,
  body: UpdateDeviceSkuRequest,
): Promise<{ sku: DeviceSkuSummary }> {
  return request(`/api/v1/devices/skus/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteDeviceSku(id: string): Promise<{ ok: true }> {
  return request(`/api/v1/devices/skus/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchDeviceProfiles(): Promise<{ profiles: DeviceProfileSummary[] }> {
  return request('/api/v1/devices/profiles');
}

export function fetchDeviceProfile(id: string): Promise<{ profile: DeviceProfileDetail }> {
  return request(`/api/v1/devices/profiles/${encodeURIComponent(id)}`);
}

export function uploadDeviceProfile(
  body: UploadDeviceProfileRequest,
): Promise<{ profile: DeviceProfileSummary }> {
  return request('/api/v1/devices/profiles', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function replaceDeviceProfile(
  id: string,
  body: ReplaceDeviceProfileRequest,
): Promise<{ profile: DeviceProfileSummary }> {
  return request(`/api/v1/devices/profiles/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteDeviceProfile(id: string): Promise<{ ok: true }> {
  return request(`/api/v1/devices/profiles/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function fetchCommandSets(): Promise<{ commandSets: CommandSetSummary[] }> {
  return request('/api/v1/command-sets');
}

export function fetchCommandSet(id: string): Promise<{ commandSet: CommandSetDetail }> {
  return request(`/api/v1/command-sets/${encodeURIComponent(id)}`);
}

export function uploadCommandSet(body: UploadCommandSetRequest): Promise<{ commandSet: CommandSetSummary }> {
  return request('/api/v1/command-sets', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function replaceCommandSet(
  id: string,
  body: ReplaceCommandSetRequest,
): Promise<{ commandSet: CommandSetSummary }> {
  return request(`/api/v1/command-sets/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteCommandSet(id: string): Promise<{ ok: true }> {
  return request(`/api/v1/command-sets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function setSkuCommandSet(skuId: string, commandSetId: string | null): Promise<{ sku: { id: string; code: string; commandSetId: string | null } }> {
  return request(`/api/v1/devices/skus/${encodeURIComponent(skuId)}/command-set`, {
    method: 'PATCH',
    body: JSON.stringify({ commandSetId }),
  });
}

export function fetchSkuFirmware(skuId: string): Promise<SkuFirmwareResponse> {
  return request(`/api/v1/devices/skus/${encodeURIComponent(skuId)}/firmware`);
}

/** Raw file body, not JSON — filename travels via a header since there's no form field to hold it. */
export function uploadSkuFirmware(skuId: string, file: File): Promise<{ ok: true; fileSize: number }> {
  return request(`/api/v1/devices/skus/${encodeURIComponent(skuId)}/firmware`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Filename': encodeURIComponent(file.name),
    },
    body: file,
  });
}

export function fetchSkuFirmwareCheckins(skuId: string): Promise<FirmwareCheckinsResponse> {
  return request(`/api/v1/devices/skus/${encodeURIComponent(skuId)}/firmware/checkins`);
}

export function triggerFirmwareOta(deviceId: string): Promise<{ id: string }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/firmware/update`, { method: 'POST' });
}

export function fetchOtaCommands(deviceId: string): Promise<OtaCommandsResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/commands`);
}

export function pingOtaDevice(deviceId: string): Promise<{ id: string }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/ping`, { method: 'POST' });
}

export function fetchOtaPingStatus(deviceId: string): Promise<PingStatusResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/ping-status`);
}

export function issueOtaCommand(deviceId: string, commandId: string): Promise<{ id: string }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/commands`, {
    method: 'POST',
    body: JSON.stringify({ commandId }),
  });
}

export function fetchOtaLog(deviceId: string): Promise<{ entries: OtaLogEntry[] }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/log`);
}

export function clearOtaLog(deviceId: string): Promise<{ ok: true; deleted: number }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/log`, { method: 'DELETE' });
}

export function fetchDeviceConfig(deviceId: string): Promise<DeviceConfigResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/config`);
}

export function issueDeviceConfigGet(deviceId: string): Promise<{ id: string }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/config/get`, { method: 'POST' });
}

export function updateDeviceConfig(deviceId: string, values: Record<string, string>): Promise<{ id: string }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/config/update`, {
    method: 'POST',
    body: JSON.stringify({ values }),
  });
}

export function updateOtaProfile(deviceId: string, body: UpdateOtaProfileRequest): Promise<UpdateOtaProfileResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/profile/update`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchDeviceAlarmConfig(deviceId: string): Promise<DeviceAlarmConfigResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/alarm`);
}

export function updateDeviceAlarmConfig(deviceId: string, body: UpdateDeviceAlarmRequest): Promise<UpdateDeviceAlarmResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/alarm/update`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchAlarmPushStatus(deviceId: string): Promise<AlarmPushStatusResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/ota/alarm/status`);
}

export function releaseDevice(
  deviceId: string,
  body: ReleaseDeviceRequest = { confirm: true },
): Promise<ReleaseDeviceResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/release`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteDevice(deviceId: string, body: DeleteDeviceRequest = { confirm: true }): Promise<{ ok: true }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function fetchDevice(id: string): Promise<{ device: DeviceSummary }> {
  return request(`/api/v1/devices/${encodeURIComponent(id)}`);
}

export function fetchDeviceProfileOptions(id: string): Promise<DeviceProfileOptionsResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(id)}/profile-options`);
}

export function updateDeviceProfile(
  id: string,
  body: UpdateDeviceProfileRequest,
): Promise<UpdateDeviceProfileResponse> {
  return request(`/api/v1/devices/${encodeURIComponent(id)}/profile`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function fetchGroups(): Promise<{ groups: DeviceGroupSummary[] }> {
  return request('/api/v1/groups');
}

export function createGroup(
  body: CreateDeviceGroupRequest,
): Promise<{ group: DeviceGroupSummary }> {
  return request('/api/v1/groups', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateGroup(
  id: string,
  body: UpdateDeviceGroupRequest,
): Promise<{ group: DeviceGroupSummary }> {
  return request(`/api/v1/groups/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteGroup(id: string): Promise<void> {
  return request(`/api/v1/groups/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function fetchGroup(id: string): Promise<{
  group: DeviceGroupSummary;
  devices: DeviceSummary[];
  available: DeviceSummary[];
}> {
  return request(`/api/v1/groups/${encodeURIComponent(id)}`);
}

export function assignDeviceGroup(
  deviceId: string,
  groupId: string | null,
): Promise<{ device: DeviceSummary }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/group`, {
    method: 'PATCH',
    body: JSON.stringify({ groupId }),
  });
}

export function fetchAudit(opts?: { limit?: number }): Promise<{ events: AuditEventSummary[] }> {
  const limit = opts?.limit ?? 100;
  const q = new URLSearchParams({ limit: String(limit) });
  return request(`/api/v1/audit?${q.toString()}`);
}

export function fetchDeviceActivity(
  deviceId: string,
): Promise<{ items: DeviceActivityItem[]; auditCount: number; messageCount: number; upstreamCount: number }> {
  return request(`/api/v1/devices/${encodeURIComponent(deviceId)}/activity`);
}

export function fetchMe(): Promise<{ user: SessionUser }> {
  return request('/api/v1/me');
}

export function fetchStaffUsers(): Promise<{ users: StaffUserSummary[] }> {
  return request('/api/v1/staff');
}

export function createStaffUser(
  body: CreateStaffUserRequest,
): Promise<{ user: StaffUserSummary; emailSent?: boolean; emailError?: string }> {
  return request('/api/v1/staff', { method: 'POST', body: JSON.stringify(body) });
}

export function updateStaffUser(
  id: string,
  body: UpdateStaffUserRequest,
): Promise<{ user: StaffUserSummary }> {
  return request(`/api/v1/staff/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function fetchClientUsers(): Promise<{ users: ClientUserSummary[] }> {
  return request('/api/v1/clients');
}

export function createClientUser(
  body: CreateClientUserRequest,
): Promise<{ user: ClientUserSummary; emailSent?: boolean; emailError?: string }> {
  return request('/api/v1/clients', { method: 'POST', body: JSON.stringify(body) });
}

export function updateClientUser(
  id: string,
  body: UpdateClientUserRequest,
): Promise<{ user: ClientUserSummary }> {
  return request(`/api/v1/clients/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function changePassword(
  body: ChangePasswordRequest,
): Promise<ChangePasswordResponse> {
  return request('/api/v1/auth/password', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchInvitePreview(token: string): Promise<InvitePreviewResponse> {
  return request(`/api/v1/auth/invite/${encodeURIComponent(token)}`);
}

export function acceptInvite(body: AcceptInviteRequest): Promise<LoginResponse> {
  return request('/api/v1/auth/invite/accept', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function resetStaffPassword(
  id: string,
): Promise<{ ok: true; emailSent: boolean; emailError?: string }> {
  return request(`/api/v1/staff/${encodeURIComponent(id)}/reset-password`, {
    method: 'POST',
    body: '{}',
  });
}

export function resetClientPassword(
  id: string,
): Promise<{ ok: true; emailSent: boolean; emailError?: string }> {
  return request(`/api/v1/clients/${encodeURIComponent(id)}/reset-password`, {
    method: 'POST',
    body: '{}',
  });
}

export function fetchUpstreamProfiles(): Promise<{ profiles: Array<{ id: string; label: string }> }> {
  return request('/api/v1/upstream/profiles');
}

export function fetchUpstreamFormats(kind?: UpstreamFormatKind): Promise<{ formats: UpstreamFormatSummary[] }> {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return request(`/api/v1/upstream/formats${q}`);
}

export function fetchUpstreamFormat(id: string): Promise<{ format: UpstreamFormatDetail }> {
  return request(`/api/v1/upstream/formats/${encodeURIComponent(id)}`);
}

export function createUpstreamFormat(
  body: CreateUpstreamFormatRequest,
): Promise<{ format: UpstreamFormatDetail }> {
  return request('/api/v1/upstream/formats', { method: 'POST', body: JSON.stringify(body) });
}

export function updateUpstreamFormat(
  id: string,
  body: UpdateUpstreamFormatRequest,
): Promise<{ format: UpstreamFormatDetail }> {
  return request(`/api/v1/upstream/formats/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteUpstreamFormat(id: string): Promise<void> {
  return request(`/api/v1/upstream/formats/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchConnections(): Promise<{ connections: ConnectionSummary[] }> {
  return request('/api/v1/connections');
}

export function createConnection(
  body: CreateConnectionRequest,
): Promise<{ connection: ConnectionSummary }> {
  return request('/api/v1/connections', { method: 'POST', body: JSON.stringify(body) });
}

export function updateConnection(
  id: string,
  body: UpdateConnectionRequest,
): Promise<{ connection: ConnectionSummary }> {
  return request(`/api/v1/connections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteConnection(id: string): Promise<void> {
  return request(`/api/v1/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchMqttBroker(): Promise<{ broker: MqttBrokerSummary | null }> {
  return request('/api/v1/mqtt-broker');
}

export function createOrUpdateMqttBroker(
  body: CreateOrUpdateMqttBrokerRequest,
): Promise<{ broker: MqttBrokerSummary }> {
  return request('/api/v1/mqtt-broker', { method: 'POST', body: JSON.stringify(body) });
}

export function deleteMqttBroker(): Promise<void> {
  return request('/api/v1/mqtt-broker', { method: 'DELETE' });
}

export function fetchMqttChannels(): Promise<{ channels: MqttChannelSummary[] }> {
  return request('/api/v1/mqtt-channels');
}

export function createMqttChannel(
  body: CreateMqttChannelRequest,
): Promise<{ channel: MqttChannelSummary }> {
  return request('/api/v1/mqtt-channels', { method: 'POST', body: JSON.stringify(body) });
}

export function updateMqttChannel(
  id: string,
  body: UpdateMqttChannelRequest,
): Promise<{ channel: MqttChannelSummary }> {
  return request(`/api/v1/mqtt-channels/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteMqttChannel(id: string): Promise<void> {
  return request(`/api/v1/mqtt-channels/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchOwnSupportGrant(): Promise<{ grant: SupportGrantSummary | null }> {
  return request('/api/v1/support/grants/active');
}

export function createSupportGrant(
  body: CreateSupportGrantRequest,
): Promise<{ grant: SupportGrantSummary }> {
  return request('/api/v1/support/grants', { method: 'POST', body: JSON.stringify(body) });
}

export function revokeSupportGrant(): Promise<void> {
  return request('/api/v1/support/grants/revoke', { method: 'POST' });
}

export function fetchActiveSupportGrants(): Promise<{ grants: SupportGrantSummary[] }> {
  return request('/api/v1/support/grants');
}

export function enterSupportGrant(tenantId: string): Promise<{ user: SessionUser }> {
  return request('/api/v1/support/enter', { method: 'POST', body: JSON.stringify({ tenantId }) });
}

export function exitSupportGrant(): Promise<{ user: SessionUser }> {
  return request('/api/v1/support/exit', { method: 'POST' });
}

export function requestForgotPassword(
  body: ForgotPasswordRequest,
): Promise<ForgotPasswordResponse> {
  return request('/api/v1/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
